import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import ScoreBars from "@/components/ScoreBars";
import {
  Lock, Loader2, Wand2, Copy, Coins, Bookmark, BookmarkCheck, Sparkles, Hash, AtSign, Globe,
} from "lucide-react";
import { toast } from "sonner";

const TIER_GROUPS = [
  { tier: "free", label: "Free Tools", hint: "No tokens needed" },
  { tier: "premium", label: "Premium Tools", hint: "Plan or 1 Studio token" },
  { tier: "advanced", label: "Advanced Tools", hint: "Plan or 1 Premium token" },
];

// Plain string-list output keys → rendered as copyable rows.
const LIST_KEYS = [
  ["taglines", "Taglines"], ["captions", "Captions"], ["bios", "Bios"], ["slogans", "Slogans"],
  ["hooks", "Hooks"], ["handles", "Handles"], ["domains", "Domains"], ["titles", "Titles"],
  ["prompts", "Visual Prompts"],
];

function Copyable({ text }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3" data-testid="studio-output-row">
      <span className="text-sm leading-relaxed">{text}</span>
      <Button size="icon" variant="ghost" className="shrink-0" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} data-testid="studio-copy-button" aria-label="Copy">
        <Copy className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

export default function Create() {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [tokens, setTokens] = useState({ studio: 0, premium: 0 });
  const [active, setActive] = useState(null);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState(null);
  const [outputId, setOutputId] = useState(null);
  const [savedOutput, setSavedOutput] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadTools = useCallback(async () => {
    try {
      const r = await api.get("/create/tools");
      setTools(r.data.tools);
      setTokens(r.data.tokens || { studio: 0, premium: 0 });
      setActive((prev) => prev ? r.data.tools.find((t) => t.id === prev.id) || r.data.tools[0] : r.data.tools[0]);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadTools(); }, [loadTools]);

  const selectTool = (t) => { setActive(t); setOutput(null); setOutputId(null); setSavedOutput(false); setInput(""); };

  const run = async (useToken = false) => {
    if (!active) return;
    if (!input.trim()) { toast.error("Enter some input"); return; }
    setBusy(true); setOutput(null); setOutputId(null); setSavedOutput(false);
    try {
      const r = await api.post("/create/run", { tool_type: active.id, input_text: input, use_token: useToken });
      setOutput(r.data.output);
      setOutputId(r.data.id);
      if (r.data.tokens) setTokens(r.data.tokens);
      if (r.data.token_spent) {
        toast.success(`Used 1 ${r.data.token_spent} token`);
        loadTools();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tool failed");
    } finally { setBusy(false); }
  };

  const saveOutput = async () => {
    if (!outputId) return;
    try {
      await api.post(`/create/save/${outputId}`, { saved: true });
      setSavedOutput(true);
      toast.success("Saved to Vault → Studio Outputs");
    } catch (e) { toast.error("Could not save"); }
  };

  const saveWord = async (word, category) => {
    try {
      await api.post("/vault", { word, category });
      toast.success(`Saved "${word}" to Vault`);
    } catch (e) { toast.error("Could not save"); }
  };

  const saveShareCard = async () => {
    if (!output?.card_text) return;
    try {
      await api.post("/vault/share-card", {
        word: input, card_text: output.card_text, bind_statement: output.bind_statement,
        signals: output.signals || [], archetype: output.archetype || "",
      });
      toast.success("Share card saved to Vault");
    } catch (e) { toast.error("Could not save"); }
  };

  const renderOutput = () => {
    if (!output) return null;
    return (
      <div className="space-y-4 fade-in" data-testid="studio-output">
        {/* Save bar */}
        {outputId && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--bone-faint)]">Output ready</span>
            <Button size="sm" variant="outline" className="glass" onClick={saveOutput} disabled={savedOutput} data-testid="studio-save-output-button">
              {savedOutput ? <><BookmarkCheck className="w-3.5 h-3.5 mr-1.5 green-text" /> Saved</> : <><Bookmark className="w-3.5 h-3.5 mr-1.5" /> Save to Vault</>}
            </Button>
          </div>
        )}

        {/* Generic string lists */}
        {LIST_KEYS.map(([key, label]) => (
          Array.isArray(output[key]) && output[key].length > 0 ? (
            <div key={key} className="space-y-2">
              <div className="mono-chip text-[var(--bone-faint)]">{label}</div>
              {output[key].map((t, i) => <Copyable key={i} text={t} />)}
            </div>
          ) : null
        ))}

        {/* Hashtags + keywords */}
        {(output.hashtags || output.keywords) && (
          <div className="grid sm:grid-cols-2 gap-4">
            {output.hashtags && (
              <div className="litra-card p-4">
                <div className="mono-chip text-[var(--bone-faint)] flex items-center gap-1.5 mb-2"><Hash className="w-3.5 h-3.5" /> Hashtags</div>
                <div className="flex flex-wrap gap-1.5">{output.hashtags.map((h, i) => (
                  <button key={i} onClick={() => { navigator.clipboard.writeText(h); toast.success("Copied"); }} className="mono-chip glass rounded-full px-2.5 py-1 hover:border-[rgba(20,90,67,0.5)] transition-colors" data-testid="studio-hashtag">{h}</button>
                ))}</div>
              </div>
            )}
            {output.keywords && (
              <div className="litra-card p-4">
                <div className="mono-chip text-[var(--bone-faint)] mb-2">SEO Keywords</div>
                <div className="flex flex-wrap gap-1.5">{output.keywords.map((k, i) => (
                  <span key={i} className="mono-chip rounded-full px-2.5 py-1 bg-white/[0.04] border border-white/[0.06]">{k}</span>
                ))}</div>
              </div>
            )}
          </div>
        )}

        {/* Color palette */}
        {Array.isArray(output.palette) && (
          <div className="litra-card p-4">
            <div className="mono-chip text-[var(--bone-faint)] mb-2">Palette</div>
            <div className="flex flex-wrap gap-2">{output.palette.map((c, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg bg-white/[0.03] border border-white/[0.06] px-2 py-1.5">
                <span className="w-5 h-5 rounded-md border border-white/20" style={{ background: c }} />
                <span className="mono-chip">{c}</span>
              </div>
            ))}</div>
          </div>
        )}

        {/* Scored names (brand / product / naming lab) */}
        {Array.isArray(output.names) && (
          <div className="space-y-3">
            {output.recommended && <div className="text-sm green-text" data-testid="studio-recommended">Recommended: <span className="font-display font-semibold">{output.recommended}</span></div>}
            <div className="grid sm:grid-cols-2 gap-3">{output.names.map((n, i) => (
              <div key={i} className="litra-card p-4" data-testid="studio-name-card">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display font-semibold">{n.input}</span>
                  <Badge variant="outline" className="green-text border-[rgba(20,90,67,0.5)] shrink-0">Brand {n.scores?.brand_usefulness}</Badge>
                </div>
                <div className="mt-1 text-xs text-[var(--bone-faint)]">{n.root} → {n.seal} · {n.archetype}</div>
                <div className="mt-2"><ScoreBars scores={n.scores} compact limit={3} /></div>
                <Button size="sm" variant="ghost" className="mt-2 h-7 px-2 text-[var(--bone-faint)] hover:green-text" onClick={() => saveWord(n.input, "brand_ideas")} data-testid="studio-save-name-button">
                  <Bookmark className="w-3.5 h-3.5 mr-1" /> Save name
                </Button>
              </div>
            ))}</div>
            {output.rationale && <div className="litra-card p-4 text-sm text-[var(--bone-muted)] leading-relaxed">{output.rationale}</div>}
          </div>
        )}

        {/* Wordbody builder letters */}
        {output.recommended_letters && (
          <div>
            <div className="text-sm text-[var(--bone-muted)] mb-2">Wanted signals: {output.wanted_signals?.join(", ")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{output.recommended_letters.map((l) => (
              <div key={l.letter} className="litra-card p-3 text-center"><div className="text-2xl font-display green-text">{l.letter}</div><div className="mono-chip text-[var(--bone-faint)]">{l.technical_name}</div></div>
            ))}</div>
            <div className="mt-3 text-sm text-[var(--bone-muted)]">{output.tip}</div>
          </div>
        )}

        {/* Share card */}
        {output.card_text && (
          <div className="litra-card p-4">
            <div className="text-sm">{output.card_text}</div>
            <div className="mt-2 mono-chip gold-text">{output.bind_statement}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">{output.signals?.map((s, i) => <span key={i} className="mono-chip glass rounded-full px-2 py-0.5">{s}</span>)}</div>
            <Button size="sm" variant="outline" className="glass mt-3" onClick={saveShareCard} data-testid="studio-save-sharecard-button"><Bookmark className="w-3.5 h-3.5 mr-1.5" /> Save share card</Button>
          </div>
        )}

        {/* Mission line */}
        {output.mission && (
          <div className="litra-card p-4"><div className="mono-chip text-[var(--bone-faint)] mb-1">Mission</div><div className="text-sm leading-relaxed">{output.mission}</div></div>
        )}

        {/* Free-text analysis */}
        {output.analysis && <div className="litra-card p-4 text-sm text-[var(--bone-muted)] leading-relaxed">{output.analysis}</div>}

        {/* Name analyzer comparisons */}
        {output.analyses && (
          <div className="space-y-2">
            {output.summary && typeof output.summary === "string" && <div className="litra-card p-4 text-sm text-[var(--bone-muted)] leading-relaxed">{output.summary}</div>}
            <div className="grid sm:grid-cols-2 gap-3">{output.analyses.map((a, i) => (
              <div key={i} className="litra-card p-4"><div className="font-display font-semibold">{a.input}</div><div className="text-xs text-[var(--bone-faint)] mb-2">{a.root} → {a.seal}</div><ScoreBars scores={a.scores} compact limit={3} /></div>
            ))}</div>
          </div>
        )}

        {/* Structured summary fallback (object form) */}
        {output.summary && typeof output.summary === "object" && output.summary?.scores && !output.names && !output.recommended_letters && (
          <div className="litra-card p-4"><div className="text-sm mb-2">{output.summary.root} → {output.summary.seal} · {output.summary.archetype}</div><ScoreBars scores={output.summary.scores} compact limit={4} /></div>
        )}
      </div>
    );
  };

  const notAllowed = active && !active.allowed_by_plan;
  const canToken = active && active.can_token;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Wand2 className="w-6 h-6 green-text" /> Studio Mode</h1>
          <p className="text-[var(--bone-muted)] text-sm">16 creator tools — names, taglines, captions, brand language and more. Save anything to your Vault.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="studio-token-balance">
          <Badge className="gold-edge gold-text bg-transparent flex items-center gap-1.5"><Coins className="w-3.5 h-3.5" /> {tokens.studio} Studio</Badge>
          <Badge className="gold-edge gold-text bg-transparent flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {tokens.premium} Premium</Badge>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        {/* Tool rail */}
        <div className="space-y-4">
          {TIER_GROUPS.map((g) => {
            const groupTools = tools.filter((t) => t.tier === g.tier);
            if (groupTools.length === 0) return null;
            return (
              <div key={g.tier} className="space-y-2">
                <div className="flex items-baseline justify-between px-1">
                  <span className="mono-chip text-[var(--bone-faint)] uppercase tracking-wider">{g.label}</span>
                  <span className="mono-chip text-[var(--bone-faint)]/70 text-[10px]">{g.hint}</span>
                </div>
                {groupTools.map((t) => (
                  <button key={t.id} onClick={() => selectTool(t)} data-testid={`studio-tool-${t.id}`}
                    className={`w-full text-left p-3 rounded-xl border transition-colors ${active?.id === t.id ? "border-[rgba(20,90,67,0.6)] bg-[rgba(20,90,67,0.12)]" : "border-white/[0.06] hover:bg-white/[0.03]"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{t.name}</span>
                      {!t.allowed_by_plan && (t.can_token
                        ? <Badge className="gold-edge gold-text bg-transparent text-[9px] px-1.5 py-0">1 {t.token_type}</Badge>
                        : <Lock className="w-3.5 h-3.5 gold-text shrink-0" />)}
                    </div>
                    <div className="text-xs text-[var(--bone-faint)] mt-0.5">{t.blurb}</div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        {/* Workspace */}
        <div className="lg:col-span-3 space-y-4">
          {active && (
            <div className="litra-card p-5">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Wand2 className="w-4 h-4 green-text" /><span className="font-display font-semibold">{active.name}</span>
                {active.tier !== "free" && <Badge className="gold-edge gold-text bg-transparent text-[10px] capitalize">{active.tier}</Badge>}
                {active.group === "Brand" && <Globe className="w-3.5 h-3.5 text-[var(--bone-faint)]" />}
                {active.group === "Naming" && <AtSign className="w-3.5 h-3.5 text-[var(--bone-faint)]" />}
              </div>

              <label className="text-sm text-[var(--bone-muted)]">{active.input_label}</label>
              <div className="flex gap-2 mt-1">
                <Input value={input} onChange={(e) => setInput(e.target.value)} className="bg-white/[0.04] border-white/[0.1]" data-testid="studio-input" placeholder={active.input_label} onKeyDown={(e) => e.key === "Enter" && !notAllowed && run(false)} />
                {notAllowed ? (
                  canToken ? (
                    <Button onClick={() => run(true)} disabled={busy} className="gold-edge gold-text bg-[rgba(176,141,60,0.12)] hover:bg-[rgba(176,141,60,0.2)] shrink-0" data-testid="studio-run-token-button">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Coins className="w-4 h-4 mr-1.5" /> Use 1 {active.token_type}</>}
                    </Button>
                  ) : (
                    <Button onClick={() => navigate("/pricing")} className="bg-[var(--primary)] hover:opacity-90 shrink-0" data-testid="studio-upgrade-button"><Lock className="w-4 h-4 mr-1.5" /> Upgrade</Button>
                  )
                ) : (
                  <Button onClick={() => run(false)} disabled={busy} className="bg-[var(--primary)] hover:opacity-90 shrink-0" data-testid="studio-run-button">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
                  </Button>
                )}
              </div>

              {notAllowed && (
                <div className="mt-3 text-xs text-[var(--bone-faint)]">
                  {canToken
                    ? <>You have <span className="gold-text">{tokens[active.token_type]} {active.token_type}</span> token{tokens[active.token_type] === 1 ? "" : "s"}. Earn more in Path &amp; Universe.</>
                    : <>Requires the <span className="text-[var(--bone)]">{(active.required_plans || []).join(" or ")}</span> plan, or a {active.token_type} token earned in Path &amp; Universe.</>}
                </div>
              )}
            </div>
          )}

          {busy && (
            <div className="litra-card p-5 space-y-3" data-testid="studio-loading">
              <div className="h-3 w-1/3 rounded bg-white/[0.06] animate-pulse" />
              <div className="h-10 rounded-lg bg-white/[0.04] animate-pulse" />
              <div className="h-10 rounded-lg bg-white/[0.04] animate-pulse" />
            </div>
          )}

          {!busy && renderOutput()}

          {!busy && !output && active && (
            <div className="text-sm text-[var(--bone-faint)] px-1">
              <Separator className="mb-4 bg-white/[0.06]" />
              Enter input above and generate. Every result can be saved to your Vault.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
